import { TILE } from '../../config/config';
import { C, THEMES, type ThemeName } from '../palette';
import { PixelBuf } from '../pixel';

/** Every 16x16 terrain tile, generated per theme. */
export function buildTileArt(theme: ThemeName): Record<string, PixelBuf> {
  const t = THEMES[theme];
  const out: Record<string, PixelBuf> = {};

  out['ground.top'] = groundTile(t.soil, t.soilDark, t.soilLight, t.grass, t.grassDark, true);
  out['ground.mid'] = groundTile(t.soil, t.soilDark, t.soilLight, t.grass, t.grassDark, false);
  out['rock'] = rockTile(theme);
  out['crystal'] = crystalTile();
  out['brick'] = brickTile(theme);
  out['used'] = usedTile();
  out['question0'] = questionTile(0);
  out['question1'] = questionTile(1);
  out['question2'] = questionTile(2);
  out['pipe.tl'] = pipeTile('tl');
  out['pipe.tr'] = pipeTile('tr');
  out['pipe.bl'] = pipeTile('bl');
  out['pipe.br'] = pipeTile('br');
  out['pipe.mt'] = pipeTile('mt');
  out['pipe.mb'] = pipeTile('mb');
  out['pipe.st'] = pipeTile('st');
  out['pipe.sb'] = pipeTile('sb');
  out['plank'] = plankTile();
  out['pole'] = poleTile();
  out['poletop'] = poleTopTile();
  out['castle'] = castleTile();
  return out;
}

function groundTile(
  soil: string,
  dark: string,
  light: string,
  grass: string,
  grassDark: string,
  top: boolean,
): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  p.rect(0, 0, TILE, TILE, soil);
  // speckle pattern (static, deterministic — no rng so tiles look identical everywhere)
  const spots: [number, number][] = [
    [2, 6],
    [7, 9],
    [12, 5],
    [4, 12],
    [10, 13],
    [14, 10],
  ];
  for (const [x, y] of spots) p.rect(x, y, 2, 2, dark);
  p.rect(0, TILE - 1, TILE, 1, dark);
  p.rect(TILE - 1, 0, 1, TILE, dark);
  if (top) {
    p.rect(0, 0, TILE, 4, grass);
    p.rect(0, 4, TILE, 1, grassDark);
    for (let x = 0; x < TILE; x += 4) {
      p.rect(x, 0, 2, 1, grassDark);
      p.px(x + 2, 1, grassDark);
    }
    p.rect(0, 5, TILE, 1, light);
  } else {
    p.rect(0, 0, TILE, 1, light);
  }
  return p;
}

function rockTile(theme: ThemeName): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  const base = theme === 'cave' ? C.crystalDark : C.stone;
  const dark = theme === 'cave' ? '#2a2050' : C.stoneDark;
  const light = theme === 'cave' ? C.crystal : C.stoneLight;
  p.rect(0, 0, TILE, TILE, base);
  p.rect(0, 0, TILE, 2, light);
  p.rect(0, TILE - 2, TILE, 2, dark);
  p.rect(TILE - 2, 0, 2, TILE, dark);
  p.rect(0, 0, 2, TILE, light);
  p.rect(4, 5, 8, 6, dark);
  p.rect(5, 6, 6, 4, base);
  return p;
}

function crystalTile(): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  p.rect(0, 0, TILE, TILE, C.crystalDark);
  p.rect(1, 1, TILE - 2, TILE - 2, C.crystal);
  p.rect(3, 3, 4, 4, C.crystalLight);
  p.rect(9, 8, 4, 5, C.crystalLight);
  p.rect(0, 0, TILE, 1, C.crystalLight);
  p.rect(0, TILE - 1, TILE, 1, '#241a44');
  return p;
}

function brickTile(theme: ThemeName): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  const base = theme === 'cave' ? '#7a5ab0' : C.brick;
  const dark = theme === 'cave' ? '#4a3080' : C.brickDark;
  const light = theme === 'cave' ? '#a88ce0' : C.brickLight;
  p.rect(0, 0, TILE, TILE, dark);
  const brick = (x: number, y: number, w: number) => {
    p.rect(x, y, w, 3, base);
    p.rect(x, y, w, 1, light);
  };
  brick(0, 0, TILE);
  brick(0, 4, 7);
  brick(8, 4, 8);
  brick(0, 8, TILE);
  brick(0, 12, 7);
  brick(8, 12, 8);
  p.rect(0, 3, TILE, 1, C.mortar);
  p.rect(0, 7, TILE, 1, C.mortar);
  p.rect(0, 11, TILE, 1, C.mortar);
  p.rect(7, 4, 1, 3, C.mortar);
  p.rect(7, 12, 1, 3, C.mortar);
  return p;
}

function usedTile(): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  p.rect(0, 0, TILE, TILE, C.usedDark);
  p.rect(1, 1, TILE - 2, TILE - 2, C.used);
  p.rect(1, 1, TILE - 2, 2, C.usedLight);
  p.rect(3, 4, TILE - 6, TILE - 7, C.usedDark);
  p.rect(4, 5, TILE - 8, TILE - 9, C.used);
  return p;
}

function questionTile(frame: number): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  const shades = [C.block, C.blockLight, C.blockDark];
  const base = shades[frame % 3];
  p.rect(0, 0, TILE, TILE, C.blockDark);
  p.rect(1, 1, TILE - 2, TILE - 2, base);
  p.rect(1, 1, TILE - 2, 2, C.blockLight);
  // rivets
  p.rect(2, 2, 2, 2, C.blockFace);
  p.rect(TILE - 4, 2, 2, 2, C.blockFace);
  p.rect(2, TILE - 4, 2, 2, C.blockFace);
  p.rect(TILE - 4, TILE - 4, 2, 2, C.blockFace);
  // original glyph: a sprout, not a question mark
  p.rect(7, 5, 2, 6, C.outline);
  p.rect(4, 6, 3, 2, C.outline);
  p.rect(9, 8, 3, 2, C.outline);
  p.rect(6, 11, 4, 1, C.outline);
  return p;
}

function pipeTile(kind: 'tl' | 'tr' | 'bl' | 'br' | 'mt' | 'mb' | 'st' | 'sb'): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  const base = C.pipe;
  const dark = C.pipeDark;
  const light = C.pipeLight;
  const left = kind === 'tl' || kind === 'bl' || kind === 'st';
  switch (kind) {
    case 'tl':
    case 'tr':
      // rim (wider lip)
      p.rect(0, 0, TILE, 5, base);
      p.rect(0, 0, TILE, 1, dark);
      p.rect(0, 4, TILE, 1, dark);
      p.rect(left ? 1 : 0, 1, 3, 3, light);
      p.rect(0, 5, TILE, TILE - 5, base);
      p.rect(left ? 2 : TILE - 5, 5, 3, TILE - 5, light);
      p.rect(left ? 0 : TILE - 2, 5, 2, TILE - 5, left ? dark : dark);
      if (left) p.rect(0, 0, 1, TILE, dark);
      else p.rect(TILE - 1, 0, 1, TILE, dark);
      break;
    case 'bl':
    case 'br':
      p.rect(0, 0, TILE, TILE, base);
      p.rect(left ? 2 : TILE - 5, 0, 3, TILE, light);
      if (left) {
        p.rect(0, 0, 2, TILE, dark);
      } else {
        p.rect(TILE - 2, 0, 2, TILE, dark);
      }
      break;
    // horizontal pipe: mouth (mt/mb) and shaft (st/sb)
    case 'mt':
      p.rect(0, 0, 5, TILE, base);
      p.rect(0, 0, 1, TILE, dark);
      p.rect(4, 0, 1, TILE, dark);
      p.rect(1, 1, 3, 3, light);
      p.rect(5, 0, TILE - 5, TILE, base);
      p.rect(5, 2, TILE - 5, 3, light);
      p.rect(0, 0, TILE, 1, dark);
      break;
    case 'mb':
      p.rect(0, 0, 5, TILE, base);
      p.rect(0, 0, 1, TILE, dark);
      p.rect(4, 0, 1, TILE, dark);
      p.rect(5, 0, TILE - 5, TILE, base);
      p.rect(5, TILE - 2, TILE - 5, 2, dark);
      p.rect(0, TILE - 1, TILE, 1, dark);
      break;
    case 'st':
      p.rect(0, 0, TILE, TILE, base);
      p.rect(0, 2, TILE, 3, light);
      p.rect(0, 0, TILE, 1, dark);
      break;
    case 'sb':
      p.rect(0, 0, TILE, TILE, base);
      p.rect(0, TILE - 2, TILE, 2, dark);
      break;
  }
  return p;
}

function plankTile(): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  p.rect(0, 0, TILE, 5, C.boot);
  p.rect(0, 0, TILE, 1, '#a06a34');
  p.rect(0, 4, TILE, 1, C.bootDark);
  p.rect(3, 1, 1, 3, C.bootDark);
  p.rect(11, 1, 1, 3, C.bootDark);
  // suspension ropes
  p.rect(1, 5, 1, 4, C.bootDark);
  p.rect(TILE - 2, 5, 1, 4, C.bootDark);
  return p;
}

function poleTile(): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  p.rect(7, 0, 3, TILE, C.stoneDark);
  p.rect(7, 0, 1, TILE, C.stoneLight);
  return p;
}

function poleTopTile(): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  p.rect(7, 4, 3, TILE - 4, C.stoneDark);
  p.rect(7, 4, 1, TILE - 4, C.stoneLight);
  p.blob(4, 0, 9, 6, C.gold);
  p.rect(6, 1, 4, 2, C.coinLight);
  return p;
}

function castleTile(): PixelBuf {
  const p = new PixelBuf(TILE, TILE);
  p.rect(0, 0, TILE, TILE, C.brickDark);
  for (let y = 0; y < TILE; y += 4) {
    const off = (y / 4) % 2 === 0 ? 0 : 4;
    for (let x = -4; x < TILE; x += 8) {
      p.rect(x + off, y, 7, 3, C.brick);
      p.rect(x + off, y, 7, 1, C.brickLight);
    }
  }
  return p;
}
